import axios from 'axios';

// Dedicated axios instance for CRM endpoints. Uses the SAME auth token and
// cookie as the main app — single sign-on across project management and CRM.
const crmApi = axios.create({ baseURL: '/api/crm', withCredentials: true });

crmApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem('flowup_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

crmApi.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      const onLogin = window.location.pathname === '/login';
      if (!onLogin) {
        localStorage.removeItem('flowup_token');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        setTimeout(() => { window.location.replace('/login'); }, 100);
      }
    }
    return Promise.reject(err.response?.data || { error: err.message });
  }
);

crmApi.get = (url, config) => crmApi({ method: 'get', url, ...config });
crmApi.post = (url, data, config) => crmApi({ method: 'post', url, data, ...config });
crmApi.put = (url, data, config) => crmApi({ method: 'put', url, data, ...config });
crmApi.delete = (url, config) => crmApi({ method: 'delete', url, ...config });

export default crmApi;

// ── Shared formatting helpers ────────────────────────────────────────────────
export const money = (v, cur = 'OMR') => {
  const n = Number(v) || 0;
  return `${cur} ${n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

export const STAGE_META = {
  lead:        { label: 'Lead',        color: '#8A93A6', bg: 'rgba(138,147,166,0.12)' },
  qualified:   { label: 'Qualified',   color: '#2563EB', bg: 'rgba(37,99,235,0.12)' },
  proposal:    { label: 'Proposal',    color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  negotiation: { label: 'Negotiation', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  won:         { label: 'Won',         color: '#16A34A', bg: 'rgba(22,163,74,0.12)' },
  lost:        { label: 'Lost',        color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
};
export const STAGE_ORDER = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

export const DOC_STATUS_META = {
  draft:            { label: 'Draft',            color: '#8A93A6', bg: 'rgba(138,147,166,0.12)' },
  pending_approval: { label: 'Pending Approval', color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  approved:         { label: 'Approved',         color: '#2563EB', bg: 'rgba(37,99,235,0.12)' },
  rejected:         { label: 'Rejected',         color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  sent:             { label: 'Sent',             color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  paid:             { label: 'Paid',             color: '#16A34A', bg: 'rgba(22,163,74,0.12)' },
  cancelled:        { label: 'Cancelled',        color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
};

export const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return (day && m && y) ? `${day}/${m}/${y}` : s;
};
export const fmtDateTime = (d) => {
  if (!d) return '';
  const s = String(d).replace('T', ' ');
  const datePart = s.slice(0, 10); const timePart = s.slice(11, 16);
  const [y, m, day] = datePart.split('-');
  return (day && m && y) ? `${day}/${m}/${y}${timePart ? ' ' + timePart : ''}` : s.slice(0, 16);
};

// DD/MM/YYYY display for dates stored as ISO yyyy-mm-dd
export const fmtDateDMY = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return (day && m && y) ? `${day}/${m}/${y}` : s;
};

// Shared option lists
export const CURRENCIES = ['OMR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'KWD', 'BHD', 'QAR'];

// Standard CRM lost-reason categories (for win/loss analytics)
export const LOST_REASONS = [
  'Price too high',
  'Lost to competitor',
  'No budget / funding',
  'No decision / went silent',
  'Timing — not now',
  'Missing features / requirements',
  'Chose to build in-house',
  'Project cancelled',
  'Poor fit / not qualified',
  'Other',
];

export const DEAL_SOURCES = [
  'Website', 'Referral', 'Cold Call', 'Email Campaign', 'Social Media',
  'Trade Show / Event', 'Existing Customer', 'Partner', 'Advertisement', 'Other',
];

// Team activity types logged against a deal at any stage
export const ACTIVITY_TYPES = [
  'Meeting', 'Call', 'Email', 'Site Visit', 'Demo / Presentation',
  'Proposal Sent', 'Follow-up', 'Negotiation', 'Note', 'Other',
];

// Gulf + common countries (extend freely)
export const COUNTRIES = [
  'Oman', 'United Arab Emirates', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain',
  'Yemen', 'Iran', 'Iraq', 'Jordan', 'Egypt', 'India', 'Pakistan',
  'United Kingdom', 'United States', 'Germany', 'France', 'Other',
];

// Common Oman cities (plus generic option)
export const CITIES = [
  'Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur', 'Ibri', 'Barka', 'Rustaq',
  'Seeb', 'Bawshar', 'Khasab', 'Ibra', 'Buraimi', 'Duqm', 'Other',
];

// Cities by country — drives the dependent city dropdown on the Accounts form.
export const CITIES_BY_COUNTRY = {
  'Oman': ['Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur', 'Ibri', 'Barka', 'Rustaq', 'Seeb', 'Bawshar', 'Khasab', 'Ibra', 'Buraimi', 'Duqm', 'Other'],
  'United Arab Emirates': ['Abu Dhabi', 'Dubai', 'Sharjah', 'Al Ain', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Other'],
  'Saudi Arabia': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Dhahran', 'Tabuk', 'Abha', 'Other'],
  'Kuwait': ['Kuwait City', 'Hawalli', 'Salmiya', 'Jahra', 'Farwaniya', 'Other'],
  'Qatar': ['Doha', 'Al Rayyan', 'Al Wakrah', 'Al Khor', 'Lusail', 'Other'],
  'Bahrain': ['Manama', 'Riffa', 'Muharraq', 'Hamad Town', 'Isa Town', 'Other'],
  'Yemen': ['Sanaa', 'Aden', 'Taiz', 'Hodeidah', 'Mukalla', 'Other'],
  'Iran': ['Tehran', 'Mashhad', 'Isfahan', 'Shiraz', 'Tabriz', 'Other'],
  'Iraq': ['Baghdad', 'Basra', 'Mosul', 'Erbil', 'Najaf', 'Other'],
  'Jordan': ['Amman', 'Zarqa', 'Irbid', 'Aqaba', 'Other'],
  'Egypt': ['Cairo', 'Alexandria', 'Giza', 'Sharm El Sheikh', 'Luxor', 'Other'],
  'India': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kochi', 'Other'],
  'Pakistan': ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Other'],
  'United Kingdom': ['London', 'Manchester', 'Birmingham', 'Glasgow', 'Leeds', 'Other'],
  'United States': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'San Francisco', 'Other'],
  'Germany': ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne', 'Other'],
  'France': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Other'],
  'Other': ['Other'],
};
